import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`);
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified!');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed');
        }
      } catch {
        setStatus('error');
        setMessage('Network error. Please try again.');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <div className="pt-32 pb-24 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="glass-card p-10 space-y-6" data-testid="verify-email-card">
            {status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 text-blue-400 mx-auto animate-spin" />
                <h2 className="text-xl font-semibold text-white">Verifying your email...</h2>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="w-14 h-14 text-green-400 mx-auto" />
                <h2 className="text-xl font-semibold text-white">Email Verified!</h2>
                <p className="text-slate-300">{message}</p>
                <Button onClick={() => navigate('/login')} className="btn-primary" data-testid="verify-login-btn">
                  Go to Login
                </Button>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="w-14 h-14 text-red-400 mx-auto" />
                <h2 className="text-xl font-semibold text-white">Verification Failed</h2>
                <p className="text-slate-300">{message}</p>
                <div className="flex gap-3 justify-center mt-4">
                  <Link to="/login">
                    <Button variant="outline" data-testid="verify-back-login-btn">
                      <Mail className="w-4 h-4 mr-2" /> Back to Login
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
